// Foreground Tracking Battery Guard.
//
// Polls getBatteryInfo() while the Tracking screen is mounted AND tracking is
// active AND the user's mode is not "off". When battery is at/below their
// threshold and the charging exception does NOT apply, either:
//   - mode "warn": show a one-shot toast (re-armed if battery recovers).
//   - mode "pause": call the provided stop() callback (reuses the same path
//     as the Stop button) and toast why.
//
// This is foreground/page-scoped only. Native background enforcement is
// queued separately (see .lovable/native-pending.md → Battery Guard).
//
// Emits structured [battery-guard] logs for every decision.
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { getBatteryInfo } from "@/lib/native/battery";
import {
  getBatteryGuardMode,
  getBatteryGuardThresholdPct,
  getTrackingAllowWhenCharging,
  type BatteryGuardMode,
} from "@/lib/prefs";

const POLL_MS = 60_000;
const REARM_BUFFER = 5; // pct above threshold before we'll warn again

export function useBatteryGuard({
  tracking,
  startedAt,
  stop,
}: {
  tracking: boolean;
  startedAt: string | null;
  stop: () => void | Promise<void>;
}) {
  const trackingRef = useRef(tracking);
  const stopRef = useRef(stop);
  useEffect(() => { trackingRef.current = tracking; }, [tracking]);
  useEffect(() => { stopRef.current = stop; }, [stop]);

  const sessionRef = useRef<{
    key: string | null;
    warnedAtOrBelow: boolean;
    paused: boolean;
  }>({ key: null, warnedAtOrBelow: false, paused: false });

  useEffect(() => {
    if (!tracking) return;

    let cancelled = false;

    async function check() {
      if (cancelled) return;
      if (!trackingRef.current) return;
      const mode: BatteryGuardMode = getBatteryGuardMode();
      if (mode === "off") return;
      const threshold = getBatteryGuardThresholdPct();
      const allowWhenCharging = getTrackingAllowWhenCharging();

      let info;
      try {
        info = await getBatteryInfo();
      } catch {
        return;
      }
      if (info.level === null) {
        console.info(
          `[battery-guard] tracking check battery=unknown threshold=${threshold} charging=${info.isCharging ?? "unknown"} allowWhenCharging=${allowWhenCharging} decision=unknown-skip`,
        );
        return;
      }

      const pct = Math.round(info.level * 100);
      const charging = info.isCharging === true;

      // Reset session state if startedAt changed.
      const key = startedAt;
      if (sessionRef.current.key !== key) {
        sessionRef.current = { key, warnedAtOrBelow: false, paused: false };
      }

      // Re-arm warn once battery recovers comfortably above threshold.
      if (sessionRef.current.warnedAtOrBelow && pct >= threshold + REARM_BUFFER) {
        sessionRef.current.warnedAtOrBelow = false;
      }

      if (pct > threshold) {
        console.info(
          `[battery-guard] tracking check battery=${pct} threshold=${threshold} charging=${charging} allowWhenCharging=${allowWhenCharging} decision=continue`,
        );
        return;
      }

      // At/below threshold — apply charging exception if configured.
      if (charging && allowWhenCharging) {
        console.info(
          `[battery-guard] tracking check battery=${pct} threshold=${threshold} charging=true allowWhenCharging=true decision=charging-bypass`,
        );
        return;
      }

      if (mode === "pause") {
        if (sessionRef.current.paused) return;
        sessionRef.current.paused = true;
        console.info(
          `[battery-guard] tracking stopped battery=${pct} threshold=${threshold} charging=${charging} reason=low-battery`,
        );
        try { await stopRef.current(); } catch { /* non-fatal */ }
        toast.warning(`Battery ${pct}% — tracking paused by Battery Guard.`);
        return;
      }

      // warn
      console.info(
        `[battery-guard] tracking check battery=${pct} threshold=${threshold} charging=${charging} allowWhenCharging=${allowWhenCharging} decision=warn`,
      );
      if (sessionRef.current.warnedAtOrBelow) return;
      sessionRef.current.warnedAtOrBelow = true;
      toast.warning(
        `Battery ${pct}% — below your ${threshold}% Tracking Battery Guard. Consider stopping tracking or charging.`,
      );
    }

    void check();
    const id = window.setInterval(() => { void check(); }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    const onFocus = () => { void check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    let removeAppListener: (() => void) | null = null;
    void import("@capacitor/app")
      .then(({ App }) => {
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void check();
        }).then((h) => {
          removeAppListener = () => { void h.remove(); };
        }).catch(() => {});
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      if (removeAppListener) removeAppListener();
    };
  }, [tracking, startedAt]);
}
